import React, { forwardRef, useCallback, useImperativeHandle, useState } from 'react';
import ImageCard from './ImageCard.js';

// Duration of the "pop the top off the stack" collapse animation that
// plays when a study is being removed from the list (e.g. after a save
// succeeds or after a delete is confirmed). Kept in sync with the CSS
// transition on the per-study wrapper below.
const REMOVE_ANIMATION_MS = 600;

// .reviews uses `gap: 20px` between flex children (see App.css). When a
// study collapses we cancel its trailing gap with a negative margin so
// the items below slide all the way up instead of leaving a 20px hole.
const REVIEWS_GAP_PX = 20;

// ImageCardList renders a list of ImageCards with a built-in "collapse
// and slide up" transition for removed studies. The animation is owned
// by the list rather than by each card so we can play it whether the
// removal originates from the card itself (in-card Delete button) or
// from the parent (e.g. UploadImages saving a study).
//
// Triggering removals:
//
//   1. Card-initiated: pass `onDelete(image)` — the list calls it, awaits
//      the returned promise (the side effect, typically an API call),
//      and then plays the animation. After the animation finishes the
//      list calls `onStudyRemoved(uid)` so the parent can drop the study
//      from its `studies` state.
//
//   2. Parent-initiated: grab a ref to the list and call
//      `listRef.current.removeStudy(uid)`. The returned promise resolves
//      once the animation has completed and `onStudyRemoved(uid)` has
//      been invoked, so the parent can sequence subsequent work
//      (e.g. saving the next study) after the row has collapsed.
const ImageCardList = forwardRef(function ImageCardList({
    studies,
    onSeriesClick,
    onDelete,
    onDownload,
    onShare,
    onTagsChange,
    confirmTagChanges,
    onStudyRemoved,
    onSeriesHydrationPrefetch,
    hydratedSeriesLookup,
    seriesHydrationProgressBySeriesUid,
    selectable = false,
    selectedStudyUIDs,
    onToggleSelect,
}, ref) {
    const [removingStudyUIDs, setRemovingStudyUIDs] = useState(() => new Set());

    const animateRemove = useCallback(async (studyKey) => {
        if (!studyKey) return;
        setRemovingStudyUIDs((prev) => {
            if (prev.has(studyKey)) return prev;
            const next = new Set(prev);
            next.add(studyKey);
            return next;
        });
        await new Promise((resolve) => setTimeout(resolve, REMOVE_ANIMATION_MS));
        if (onStudyRemoved) onStudyRemoved(studyKey);
        setRemovingStudyUIDs((prev) => {
            if (!prev.has(studyKey)) return prev;
            const next = new Set(prev);
            next.delete(studyKey);
            return next;
        });
    }, [onStudyRemoved]);

    useImperativeHandle(ref, () => ({
        removeStudy: animateRemove,
    }), [animateRemove]);

    // Wrap the parent's onDelete so the in-card Delete button triggers
    // the same collapse animation as imperative removals. The parent's
    // onDelete should do the side effect (e.g. API call) only; the list
    // owns the animation timing and the subsequent state update via
    // onStudyRemoved. The animation is intentionally not awaited so the
    // ImageCard's delete modal can close as soon as the side effect
    // resolves rather than lingering for the full transition.
    const handleCardDelete = onDelete
        ? async (image) => {
            await onDelete(image);
            animateRemove(image.studyInstanceUID);
        }
        : undefined;

    return (
        <div className="reviews">
            {studies.map((study) => {
                const isRemoving = removingStudyUIDs.has(study.studyInstanceUID);
                return (
                    <div
                        key={study.studyInstanceUID}
                        style={{
                            display: 'grid',
                            gridTemplateRows: isRemoving ? '0fr' : '1fr',
                            opacity: isRemoving ? 0 : 1,
                            transform: isRemoving ? 'translateY(-12px)' : 'none',
                            marginBottom: isRemoving ? `-${REVIEWS_GAP_PX}px` : 0,
                            transition: `grid-template-rows ${REMOVE_ANIMATION_MS}ms ease, opacity ${Math.round(REMOVE_ANIMATION_MS * 0.7)}ms ease, transform ${REMOVE_ANIMATION_MS}ms ease, margin-bottom ${REMOVE_ANIMATION_MS}ms ease`,
                            pointerEvents: isRemoving ? 'none' : 'auto',
                        }}
                    >
                        <div style={{ overflow: 'hidden', minHeight: 0 }}>
                            <ImageCard
                                image={study}
                                onSeriesClick={onSeriesClick}
                                onDelete={handleCardDelete}
                                onDownload={onDownload}
                                onShare={onShare}
                                onTagsChange={onTagsChange}
                                confirmTagChanges={confirmTagChanges}
                                onSeriesHydrationPrefetch={onSeriesHydrationPrefetch}
                                hydratedSeriesLookup={hydratedSeriesLookup}
                                seriesHydrationProgressBySeriesUid={seriesHydrationProgressBySeriesUid}
                                selectable={selectable}
                                selected={!!selectedStudyUIDs?.has(study.studyInstanceUID)}
                                onToggleSelect={onToggleSelect}
                            />
                        </div>
                    </div>
                );
            })}
        </div>
    );
});

export default ImageCardList;
